import type { TagColorMode, TagVariant } from '@treneo/shared/constants';
import { TagType } from '@treneo/shared/constants';
import { useTagConfig } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { AddIcon, IconSizes, RemoveIcon } from '@treneo/shared/icons';
import { Colors, Spacing } from '@treneo/shared/themes';
import { Box, IconButton, Paper, Skeleton, styled, Typography } from '@treneo/shared/ui';

import { CourseChip, getTagColor } from '../../course-tag';

import type { CourseTagWithTranslation, TagTypeType } from '../types';

const TagOptionsContainer = styled(Paper)`
  border: 2px solid ${Colors.darkGrey};
  border-radius: 8px;
  box-shadow:
    0 8px 24px rgba(62, 66, 71, 0.12),
    0 4px 12px rgba(62, 66, 71, 0.08);
  background-color: ${Colors.white};
`;

const TagOptionItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${Spacing.xsmall};
  padding: 6px 12px;
  border-bottom: 1px solid ${Colors.lightGrey};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${Colors.lightGrey};
  }
`;

const ClickableTagChip = styled(CourseChip)`
  cursor: pointer;
  pointer-events: auto;
`;

const StyledIncludeButton = styled(IconButton)`
  color: ${Colors.green} !important;

  &:hover {
    background-color: ${Colors.green}10 !important;
  }

  &:focus {
    outline: 2px solid ${Colors.green} !important;
    outline-offset: 2px;
  }
`;

const StyledExcludeButton = styled(IconButton)`
  color: ${Colors.red} !important;

  &:hover {
    background-color: ${Colors.red}10 !important;
  }

  &:focus {
    outline: 2px solid ${Colors.red} !important;
    outline-offset: 2px;
  }
`;

type TagOptionsProps = Readonly<{
  availableTags: CourseTagWithTranslation[];
  selectedTagsCount: number;
  maxTags: number;
  isLoading: boolean;
  onTagAdd: (tag: CourseTagWithTranslation, type: TagTypeType) => void;
  getTagTranslation: (tag: CourseTagWithTranslation) => string;
}>;

export const TagOptions = ({
  availableTags,
  selectedTagsCount,
  maxTags,
  isLoading,
  onTagAdd,
  getTagTranslation
}: TagOptionsProps) => {
  const { t } = useAppTranslations();
  const { tagVariant, tagColorMode, tagStyling } = useTagConfig();

  if (isLoading) {
    return (
      <TagOptionsContainer>
        <Box sx={{ p: 1 }} role='status' aria-live='polite'>
          <Typography sx={{ sr: 'only' }}>{t('coursesPage.loadingTags')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} />
          </Box>
        </Box>
      </TagOptionsContainer>
    );
  }

  if (availableTags.length === 0) {
    return (
      <TagOptionsContainer>
        <Box sx={{ p: 1, textAlign: 'center' }} role='status'>
          <Typography variant='body2' sx={{ color: Colors.darkGrey, fontSize: '12px' }}>
            {selectedTagsCount >= maxTags
              ? t('coursesPage.maxTagsReached', `Maximum ${maxTags} tags allowed`)
              : t('coursesPage.noTagsAvailable')}
          </Typography>
        </Box>
      </TagOptionsContainer>
    );
  }

  const renderedOptions = availableTags.map((tag, index) => {
    if (!tag?.value) {
      return null;
    }

    const tagColor = getTagColor(
      tag.value,
      tagVariant as TagVariant,
      tagStyling.uniformColor,
      tagColorMode as TagColorMode
    );
    const tagLabel = getTagTranslation(tag);
    const isDisabled = selectedTagsCount >= maxTags;

    const handleTagClick = () => {
      if (!isDisabled) {
        onTagAdd(tag, TagType.Include);
      }
    };

    return (
      <TagOptionItem
        key={tag.value}
        role='option'
        aria-selected='false'
        aria-posinset={index + 1}
        aria-setsize={availableTags.length}
      >
        <StyledIncludeButton
          size='small'
          onClick={() => onTagAdd(tag, TagType.Include)}
          aria-label={t('coursesPage.includeTagWithName', { tagName: tagLabel })}
          disabled={isDisabled}
          data-testid='tag-include-button'
        >
          <AddIcon fontSize={IconSizes.small} aria-hidden='true' />
        </StyledIncludeButton>
        <ClickableTagChip
          backgroundColor={tagColor}
          variant={tagVariant as TagVariant}
          uniformColor={tagStyling.uniformColor}
          borderSize={tagStyling.borderSize}
          fontSize={tagStyling.fontSize}
          colorMode={tagColorMode as TagColorMode}
          onClick={handleTagClick}
          aria-label={t('coursesPage.includeTagWithName', { tagName: tagLabel })}
          style={{ opacity: isDisabled ? 0.6 : 1 }}
        >
          {tagLabel}
        </ClickableTagChip>
        <StyledExcludeButton
          size='small'
          onClick={() => onTagAdd(tag, TagType.Exclude)}
          aria-label={t('coursesPage.excludeTagWithName', { tagName: tagLabel })}
          disabled={isDisabled}
          data-testid='tag-exclude-button'
        >
          <RemoveIcon fontSize={IconSizes.small} aria-hidden='true' />
        </StyledExcludeButton>
      </TagOptionItem>
    );
  });

  return (
    <TagOptionsContainer
      id='search-tags-listbox'
      role='listbox'
      aria-label={t('coursesPage.availableTagsListbox')}
    >
      {renderedOptions}
    </TagOptionsContainer>
  );
};
